<template>
	<!-- 添加loading遮罩层 -->
	<div v-loading="isLoading" element-loading-text="加载中..." element-loading-background="rgba(255, 255, 255, 0.8)">
		<div class="news">
			<carousel v-if="configData?.news_banner?.length > 0" :imgList="configData?.news_banner" class="carousel"></carousel>
			<div class="news_content">
				<div v-if="newsList?.length > 0" class="news-slider-container">
					<el-carousel
						:interval="5000"
						:autoplay="true"
						indicator-position="bottom"
						height="280px"
						arrow="never"
						class="news-slider"
					>
						<el-carousel-item v-for="(news, index) in newsList?.slice(0, 3)" :key="index">
							<div class="news-item" @click="toDetail(news)">
								<!-- 左侧图片 -->
								<div class="left-image">
									<div class="news_image_container">
										<img :src="news?.image" :alt="news?.title" class="news-image" @error="handleImageError"/>
									</div>
								</div>
								<!-- 右侧信息 -->
								<div class="right-info">
									<div v-if="news?.tag" class="news-tag" :class="news.tag === '媒体关注' ? 'media_tag' : 'news_tag'">{{ news.tag }}</div>
	
									<!-- 轮播区标题：超过一行显示tooltip -->
									<el-tooltip
										v-if="news.titleOverflow"
										:content="news.title"
										placement="top"
										popper-class="tooltip-custom"
									>
										<h3 ref="sliderTitleRefs" :data-index="index" class="news-title">
											{{ news.title }}
										</h3>
									</el-tooltip>
									<h3
										v-else-if="news?.title"
										ref="sliderTitleRefs"
										:data-index="index"
										class="news-title"
									>
										{{ news?.title }}
									</h3>
	
									<div v-if="news?.publishTime" class="news-date">{{ dayjs(news.publishTime).format('YYYY-MM-DD') }}</div>
	
									<!-- 轮播区内容：超过两行显示tooltip -->
									<el-tooltip
										v-if="news.contentOverflow"
										:content="news.content"
										placement="top"
										popper-class="tooltip-custom"
									>
										<p ref="sliderContentRefs" :data-index="index" class="news-content">
											{{ news.content }}
										</p>
									</el-tooltip>
									<p
										v-else-if="news?.content"
										ref="sliderContentRefs"
										:data-index="index"
										class="news-content"
									>
										{{ news?.content }}
									</p>
	
									<div class="view-detail bm bm-ac">
										查看详情
										<el-icon class="el-icon--right"><ArrowRight /></el-icon>
									</div>
								</div>
							</div>
						</el-carousel-item>
					</el-carousel>
				</div>
				<div class="news_item_more" v-if="newsMoreList?.length > 0">
					<div class="news_item bm bm-ac" v-for="(news, index) in newsMoreList" :key="index" @click="toDetail(news)">
						<!-- 左侧图片 -->
						<div class="left_image">
							<div class="news_image_container">
								<img :src="news?.image" :alt="news.title" class="news-image" @error="handleImageError"/>
							</div>
						</div>
	
						<!-- 右侧信息 -->
						<div class="right_info">
							<div v-if="news?.tag" class="news-tag" :class="news.tag === '媒体关注' ? 'media_tag' : 'news_tag'">{{ news.tag }}</div>
	
							<!-- 更多新闻标题：超过一行显示tooltip -->
							<el-tooltip
								v-if="news.titleOverflow"
								:content="news.title"
								placement="top"
							>
								<h3 ref="moreTitleRefs" :data-index="index" class="news-title">
									{{ news.title }}
								</h3>
							</el-tooltip>
							<h3
								v-else-if="news?.title"
								ref="moreTitleRefs"
								:data-index="index"
								class="news-title"
							>
								{{ news?.title }}
							</h3>
	
							<!-- 更多新闻内容：超过两行显示tooltip -->
							<el-tooltip
								v-if="news.contentOverflow"
								:content="news.content"
								placement="top"
							>
								<p ref="moreContentRefs" :data-index="index" class="news-content">
									{{ news.content }}
								</p>
							</el-tooltip>
							<p
								v-else-if="news?.content"
								ref="moreContentRefs"
								:data-index="index"
								class="news-content"
							>
								{{ news?.content }}
							</p>
	
							<div v-if="news?.publishTime" class="news-date bm bm-ac">
								<img class="date_icon" src="@/assets/date.png" alt="logo" />
								<div>{{ dayjs(news.publishTime).format('YYYY-MM-DD') }}</div>
							</div>
						</div>
						<div class="view-detail-right" @click="toDetail(news)">
							<img class="to_right" src="@/assets/to_right.png" alt="logo" />
						</div>
					</div>
				</div>
			</div>
			<!-- 暂无数据 -->
			<div v-if="newsList?.length === 0 && newsMoreList?.length === 0 && isLoading === false" class="empty_box bm bm-ver bm-ac bm-pc">
				<img src="@/assets/empty_icon.png" alt="">
				<div class="empty_text">
					暂无数据
				</div>
			</div>
		</div>
	</div>
</template>

<script setup lang="ts">
import { ref, onMounted, nextTick, onUnmounted, computed } from 'vue'
import { ArrowRight } from '@element-plus/icons-vue'
import { checkEllipsisAsync } from '@/utils/overflowUtil' // 导入工具函数
import carousel from '@/components/carousel.vue'
import dayjs from 'dayjs'

const sliderTitleRefs = ref<HTMLElement[]>([])
const sliderContentRefs = ref<HTMLElement[]>([])
const moreTitleRefs = ref<HTMLElement[]>([])
const moreContentRefs = ref<HTMLElement[]>([])

// 检查所有溢出
const checkAllOverflow = async () => {
  try {
    // 检查轮播区标题（单行）
    const sliderTitleResults = await checkEllipsisAsync('.news-slider .news-title', 1, 300)
    sliderTitleResults.forEach((result, index) => {
      if (newsList.value[index]) {
        newsList.value[index].titleOverflow = result.isOverflow
      }
    })

    // 检查轮播区内容（两行）
    const sliderContentResults = await checkEllipsisAsync('.news-slider .news-content', 2, 300)
    sliderContentResults.forEach((result, index) => {
      if (newsList.value[index]) {
        newsList.value[index].contentOverflow = result.isOverflow
      }
    })

    // 检查更多新闻标题（单行）
    const moreTitleResults = await checkEllipsisAsync('.news_item_more .news-title', 1, 300)
    moreTitleResults.forEach((result, index) => {
      if (newsMoreList.value[index]) {
        newsMoreList.value[index].titleOverflow = result.isOverflow
      }
    })

    // 检查更多新闻内容（两行）
    const moreContentResults = await checkEllipsisAsync('.news_item_more .news-content', 2, 300)
    moreContentResults.forEach((result, index) => {
      if (newsMoreList.value[index]) {
        newsMoreList.value[index].contentOverflow = result.isOverflow
      }
    })

    // 调试信息
    console.log('轮播区新闻检查完成')
    console.log('更多新闻检查完成')
  } catch (error) {
    console.error('检查省略号时出错:', error)
  }
}
// 窗口大小改变时重新检查
const handleResize = () => {
  checkAllOverflow()
}
// 初始化检查
onMounted(() => {
  nextTick(() => {
    checkAllOverflow()
  })
  // 添加窗口大小改变监听
  window.addEventListener('resize', handleResize)
})
// 组件卸载时移除监听器
onUnmounted(() => {
  window.removeEventListener('resize', handleResize)
})

const router = useRouter()
import { useConfigStore } from "@/stores/index.ts";
const store = useConfigStore()

const toDetail = (item) => {
	console.log(item, "newsDetail");
	// 将新闻详情存储到store中
	store.setNewsDetail({
		title: item.title,
		date: item.publishTime,
		content: item.richContent,
	})
  router.push({
    path: '/newsDetail',
  })
}

const configData = computed(() => {
	console.log(store.configData,"store.configData")
	return store.configData
})
// 添加loading状态
const isLoading = ref(false)
import { getNewsList } from '@/apis'
let newsList = ref([])
let newsMoreList = ref([])
onBeforeMount(async () => {
	isLoading.value = true
	const res = await getNewsList("news_bar")
	if(res && res.msgCode === "100") {
		newsList.value = res.responseData?.map(item => {
			return {
				...item,
				titleOverflow: false,
				contentOverflow: false
			}
		})
	}
	const resMore = await getNewsList("news_list")
	if(resMore && resMore.msgCode === "100") {
		newsMoreList.value = resMore.responseData?.map(itemMore => {
			return {
				...itemMore,
				titleOverflow: false,
				contentOverflow: false
			}
		})
	}
	isLoading.value = false
})

// 图片加载错误处理
const handleImageError = (event: Event) => {
  const target = event.target as HTMLImageElement;
  // 设置默认图片路径
  target.src = new URL('@/assets/news_default_photo.png', import.meta.url).href;
}

</script>

<style lang="styl" scoped>
.news{
	width 100%
	min-height calc(100% - 70px - 220px)
	background-color #fff
	min-width 1200px
	.carousel {
	    margin-bottom 60px
	}
	.news_content{
		padding: 20px 140px 90px 140px
		width 100%
		.news-slider-container {
			width: 100%;
			height: 300px;
			margin: 0 auto;
			position: relative;
			.news-item {
				display: flex;
				width: 100%;
				height: 100%;
				cursor: pointer;
				.left-image {
					.news_image_container{
						width: 441px;
						height: 0;
						padding-bottom: 62.5%;
						position: relative;
						overflow: hidden;
						.news-image {
							position: absolute;
							top: 0;
							left: 0;
							width: 100%;
							height: 100%;
							object-fit: cover;
						}
					}
				}
				.right-info {
					width: 60%;
					padding: 30px 40px;
					box-sizing: border-box;
					.news-tag {
						display: inline-block;
						padding: 4px 10px;
						font-size: 12px;
						border-radius: 4px;
						margin-bottom: 16px;
					}
					.news-title {
						font-size: 16px;
						font-weight: bold;
						color: #333;
						line-height: 1.5;
						margin: 0 0 8px 0;
						/* 单行显示，超出显示省略号 */
						white-space: nowrap;
						overflow: hidden;
						text-overflow: ellipsis;
					}
					.news-date {
						font-size: 13px;
						color: #666666;
						margin-bottom: 14px;
					}
					.news-content {
						font-size: 13px;
						color: #666;
						line-height: 1.6;
						margin: 0 0 20px 0;
						/* 两行显示，超出显示省略号 */
						display: -webkit-box;
						-webkit-box-orient: vertical;
						-webkit-line-clamp: 2;
						overflow: hidden;
						text-overflow: ellipsis;
						word-wrap: break-word;
						word-break: break-all;
						-webkit-box-flex: 1;
						max-height: calc(1.6em * 2);
					}
					.view-detail {
						font-size: 13px;
						cursor: pointer;
						line-height: 13px;
					}
				}
			}
			.news-item:hover{
				.right-info{
					.news-title {
						color: #0055A2;
					}
					.view-detail {
						color: #0055A2;
						text-decoration-thickness: 2px;
					}
				}
			}
			:deep(.el-carousel__indicators){
				position: absolute;
				bottom: 10px;
				left: 350px;
			}
		}

		.news_tag{
			color: #1989FA;
			background: rgba(24,106,241,0.06);
		}
		.media_tag{
			color: #3D49D7 ;
			background: rgba(61,73,215,0.06);
		}

		.news_item_more{
			margin-top: 40px;
			.news_item{
				display: flex;
				padding-bottom: 29px;
				border-bottom: 1px solid #E6E6E6;
				margin-bottom: 29px;
				cursor: pointer;
				.left_image{
					.news_image_container{
						width: 220px;
						height: 0;
						padding-bottom: 74.07%;
						position: relative;
						overflow: hidden;
						.news-image {
							position: absolute;
							top: 0;
							left: 0;
							width: 100%;
							height: 100%;
							object-fit: cover;
						}
					}
				}
				.right_info {
					width: calc(100% - 220px);
					flex: 1;
					padding: 0 30px;
					box-sizing: border-box;
					.news-tag {
						display: inline-block;
						padding: 4px 10px;
						font-size: 12px;
						border-radius: 4px;
						margin-bottom: 12px;
					}
					.news-title {
						font-size: 15px;
						font-weight: bold;
						color: #333;
						line-height: 1.4;
						margin: 0 0 8px 0;
						/* 单行显示，超出显示省略号 */
						white-space: nowrap;
						overflow: hidden;
						text-overflow: ellipsis;
					}
					.news-date {
						font-size: 12px;
						color: #666666;
						margin-bottom: 12px;
						.date_icon{
							height: 12px;
							width: 12px;
							margin-right: 7px;
						}
						div{
							line-height: 12px;
						}
					}
					.news-content {
						font-size: 13px;
						color: #666;
						line-height: 1.5;
						margin: 0 0 18px 0;
						/* 两行显示，超出显示省略号 */
						display: -webkit-box;
						-webkit-box-orient: vertical;
						-webkit-line-clamp: 2;
						overflow: hidden;
						text-overflow: ellipsis;
						word-wrap: break-word;
						word-break: break-all;
						-webkit-box-flex: 1;
						max-height: calc(1.5em * 2);
					}
				}
				.view-detail-right {
					.to_right{
						width: 0
						cursor: pointer;
					}
				}
			}
			.news_item:hover{
				border-bottom: 1px solid #0055A2;
				.news-title {
					color: #0055A2;
				}
				.view-detail-right {
					.to_right{
						width: 19px;
						height: 12px;
					}
				}
			}
			/* .news_item:last-child{
				border-bottom: none;
			} */
		}
	}

	@media screen and (max-width: 1400px) {
		/* .news_content {
			.news-slider-container {
				.news-item {
					.left-image {
						.news_image_container {
							width: 350px;
						}
					}
					.right-info{
						padding 10px 40px
					}
				}
				:deep(.el-carousel__indicators){
					position: absolute;
					bottom: 60px;
					left: 280px;
				}
			}
			.news_item_more{
				margin-top: 0
			}
		} */
	}
	.empty_box{
		width: 100%;
		height: 100%;
		min-height: 30vh;
	}
}
</style>